// frontend/src/services/memberService.js

const API = process.env.REACT_APP_API_BASE || '';


/**
 * Crear miembro (relación group ↔ person).
 * Endpoint: POST /api/member
 *
 * Headers:
 *  - x-access-hash: string   (requerido; flujo por hash)
 *  - Authorization: Bearer <jwt> (NO enviar en creación por hash-flow)
 *
 * Body (ejemplo):
 *  {
 *    "national_id": "...",
 *    "passport": "...",
 *    "first_name": "...",
 *    "last_name": "...",
 *    "birthdate": "YYYY-MM-DD",
 *    "gender": "...",
 *    "nationality": "...",
 *    "contact": "...",
 *    ...
 *  }
 *
 * Respuesta:
 *  - Objeto aplanado con:
 *      id (= member_id), member_id, person_id, group_id, created_by_hash,
 *      campos de persona (first_name, last_name, etc.)
 */
export async function createMember(data, hash) {
  const res = await fetch(`${API}/api/member`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-access-hash': hash, // Hash-flow: sin Authorization
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });

  if (!res.ok) {
    let json = {};
    try { json = await res.json(); } catch { }
    const serverMsg =
      json?.message ||
      (res.status === 400
        ? 'Datos incompletos o inválidos para crear la persona.'
        : res.status === 409
          ? 'La persona ya existe para este grupo o país.'
          : 'No se pudo crear el miembro.');
    const err = new Error(serverMsg);
    err.status = res.status;
    if (json?.code) err.code = json.code;
    if (json?.errors) err.fields = json.errors;
    err.personName = json?.name || '';
    throw err;
  }
  return res.json();
}

/**
 * Obtener miembro por ID de groups_members.
 * Endpoint: GET /api/member/id/:id
 *
 * Headers:
 *  - x-access-hash: string   (requerido; el controller hidrata acceso/grupo 
 *                            con este header)
 *  - Authorization: Bearer <jwt> (opcional)
 *
 * Respuesta:
 *  - Objeto aplanado con:
 *      id (= member_id), member_id, person_id, group_id, created_by_hash,
 *      campos de persona (first_name, last_name, national_id, etc.)
 */
export async function getMemberById(id, accessHash, jwt) {
  if (!id) throw new Error('Missing member id');

  const res = await fetch(`${API}/api/member/id/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(accessHash ? { 'x-access-hash': accessHash } : {}),
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'Failed to load member');
  }
  return res.json();
}

/**
 * Eliminar miembro (soft delete) por ID de groups_members.
 * Endpoint: DELETE /api/member/:id
 *
 * Headers:
 *  - x-access-hash: string   (requerido)
 *  - Authorization: Bearer <jwt> (opcional)
 *
 * Respuesta:
 *  { "message": "Member deleted successfully" }
 */
export async function deleteMember(id, hash) {
  const res = await fetch(`${API}/api/member/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'x-access-hash': hash,
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.message || 'Error deleting member');
  }
  return res.json();
}

/**
 * Actualizar miembro por ID (edición SIEMPRE por member_id).
 * Endpoint: PUT /api/member/:id
 *
 * Headers:
 *  - x-access-hash: string   (requerido)
 *  - Authorization: Bearer <jwt> (opcional)
 *
 * Body:
 *  - Solo campos permitidos para actualizar los datos asociados de persona 
 *    (el backend decide):
 *    {
 *      "national_id"?, "passport"?, "first_name"?, "middle_name"?, 
 *      "last_name"?, "second_last_name"?, "gender"?, "birthdate"?, 
 *      "nationality"?, "contact"?
 *    }
 *
 * Respuesta:
 *  - Objeto aplanado (mismo formato que GET /api/member/id/:id).
 */
export async function updateMember(id, payload, accessHash, jwt) {
  if (!id) throw new Error('Missing member id');
  const res = await fetch(`${API}/api/member/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(accessHash ? { 'x-access-hash': accessHash } : {}),
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail;
    try { detail = JSON.parse(text); } catch { detail = { message: text }; }
    const err = new Error(detail?.message || `updateMember failed: ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  const data = await res.json();

  return {
    ...data,
    validation_status:
      data.validation_status ??
      data.validationStatus ??
      data.person?.validation_status ??
      data.person?.validationStatus ??
      null,
    validation_source:
      data.validation_source ??
      data.validationSource ??
      data.person?.validation_source ??
      data.person?.validationSource ??
      null,
  };
}